import * as signalR from "@microsoft/signalr";
import { getHubBase } from "@/shared/lib/signalr-helpers";
import { isSignalRTransientNoise } from "@/shared/lib/signalr/transient-noise";

export interface UserNotification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  actionUrl?: string;
  isRead: boolean;
  createdAt: string;
  priority?: string;
  eventType?: string;
  playSound?: boolean;
}

/**
 * Полезная нагрузка MatchAutoLobbyTrigger. Помимо обязательных matchId/scheduledAt
 * бэкенд шлёт данные турнира — они нужны Tauri-воркеру, чтобы поднять нужный тип лобби.
 */
export interface MatchAutoLobbyTriggerPayload {
  matchId: string;
  scheduledAt: string;
  tournamentId?: string;
  tournamentType?: string;
  bestOf?: number;
}

export interface FriendActivityAddedPayload {
  id: string;
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  activityType: string;
  title: string;
  description?: string | null;
  relatedEntityId?: string | null;
  relatedEntityType?: string | null;
  createdAt: string;
}

export class NotificationConnection {
  private connection: signalR.HubConnection;
  private startPromise: Promise<void> | null = null;
  private stopRequested = false;
  private retryTimer: number | null = null;
  private startAttempt = 0;

  /**
   * После скольких подряд неудачных попыток первого подключения о них всё же надо сказать
   * в консоль. Одиночный 502 в окно перезапуска бэкенда — штатная жизнь соединения
   * (KAN-600), спамить им незачем; хаб, который не встаёт минуту подряд, — уже дефект,
   * и он должен быть виден, а не искаться неделю (KAN-919).
   */
  private static readonly LOUD_AFTER_ATTEMPTS = 4;

  constructor() {
    const hubUrl = `${getHubBase()}/notifications`;
    const retryPolicy: signalR.IRetryPolicy = {
      nextRetryDelayInMilliseconds: (ctx) => {
        const n = ctx.previousRetryCount;
        const base = n === 0 ? 0 : Math.min(60_000, 1000 * 2 ** Math.min(n, 6));
        return base + Math.floor(Math.random() * 750);
      },
    };
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: true,
        skipNegotiation: false,
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents,
      })
      .configureLogging(signalR.LogLevel.None)
      .withAutomaticReconnect(retryPolicy)
      .build();
  }

  private scheduleRetry(): void {
    if (this.stopRequested) return;
    if (this.retryTimer) return;
    const base = Math.min(60_000, 1000 * 2 ** Math.min(this.startAttempt, 6));
    const jitter = Math.floor(Math.random() * 750);
    const delay = Math.min(60_000, base + jitter);
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      void this.start();
    }, delay);
  }

  async start(): Promise<void> {
    this.stopRequested = false;
    const state = this.connection.state;
    if (state === signalR.HubConnectionState.Connected) return;
    if (this.startPromise) return this.startPromise;

    this.startAttempt += 1;
    this.startPromise = this.connection
      .start()
      .then(() => {
        this.startAttempt = 0;
      })
      .catch((err) => {
        if (this.stopRequested) return;

        // KAN-919: ретрай ОБЯЗАН быть безусловным. Раньше транзиентная ошибка
        // (404/502/503/504, оборванное рукопожатие) просто гасилась, и на этом всё
        // заканчивалось: `withAutomaticReconnect` покрывает потерю уже установленного
        // соединения, а не провал первичного `start()`. Один 502 от шлюза в окно выката —
        // и колокольчик мёртв до перезагрузки страницы, причём молча.
        // Подавление шума осталось, но теперь оно про ЛОГИ, а не про попытки.
        if (!isSignalRTransientNoise(err)) {
          console.error("[SignalR] Не удалось подключиться к NotificationHub:", err);
        } else if (this.startAttempt >= NotificationConnection.LOUD_AFTER_ATTEMPTS) {
          console.warn(
            `[SignalR] NotificationHub не поднимается: попыток подряд ${this.startAttempt}`,
            err,
          );
        }

        this.scheduleRetry();
      })
      .finally(() => {
        this.startPromise = null;
      });

    return this.startPromise;
  }

  async stop(): Promise<void> {
    try {
      this.stopRequested = true;
      if (this.retryTimer) {
        window.clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
      await this.connection.stop();
    } catch (err) {
      console.error("[SignalR] Ошибка отключения от NotificationHub:", err);
    }
  }

  onReceiveNotification(
    callback: (notification: UserNotification) => void,
  ): void {
    this.connection.on("ReceiveNotification", callback);
  }

  /**
   * KAN-919: подтверждение приёма. Сервер не может узнать, дошло ли уведомление до вкладки:
   * `Clients.User(...)` для человека без живых соединений завершается успехом, а с
   * Redis-backplane это вообще publish. Поэтому «доставлено» ставится по этому вызову —
   * не подтвердили, значит уведомление уедет досылкой при следующем подключении.
   *
   * Fire-and-forget: ответа не ждём, сбой не показываем. Не дошло подтверждение —
   * уведомление просто останется в очереди досылки, что хуже лишнего тоста, но не потери.
   */
  acknowledgeNotification(notificationId: string): void {
    if (!notificationId) return;
    if (this.connection.state !== signalR.HubConnectionState.Connected) return;
    this.connection.send("AcknowledgeNotification", notificationId).catch(() => {});
  }

  /**
   * KAN-919: переподключение. Пропущенное за время разрыва сервер дошлёт сам, но лента и
   * бейдж живут в React Query с ненулевым staleTime и без refetch по фокусу — без явной
   * инвалидации вкладка продолжает показывать состояние на момент обрыва.
   */
  onReconnected(callback: () => void): void {
    this.connection.onreconnected(() => callback());
  }

  offReceiveNotification(): void {
    this.connection.off("ReceiveNotification");
  }

  onFriendActivityAdded(callback: (payload: FriendActivityAddedPayload) => void): void {
    this.connection.on("FriendActivityAdded", callback);
  }

  offFriendActivityAdded(): void {
    this.connection.off("FriendActivityAdded");
  }

  // Cross-device read-sync: события от backend когда юзер взаимодействует с
  // уведомлениями на одном из своих устройств. Все остальные подписываются и
  // обновляют локальное состояние без повторного API-запроса.

  /** Прочитано конкретное уведомление на другом устройстве. */
  onNotificationRead(callback: (notificationId: string) => void): void {
    this.connection.on("NotificationRead", callback);
  }

  offNotificationRead(): void {
    this.connection.off("NotificationRead");
  }

  /** Все уведомления помечены как прочитанные на другом устройстве. */
  onAllNotificationsRead(callback: () => void): void {
    this.connection.on("AllNotificationsRead", callback);
  }

  offAllNotificationsRead(): void {
    this.connection.off("AllNotificationsRead");
  }

  /** Уведомление удалено на другом устройстве. */
  onNotificationDeleted(callback: (notificationId: string) => void): void {
    this.connection.on("NotificationDeleted", callback);
  }

  offNotificationDeleted(): void {
    this.connection.off("NotificationDeleted");
  }

  /** Все уведомления очищены на другом устройстве. */
  onAllNotificationsCleared(callback: () => void): void {
    this.connection.on("AllNotificationsCleared", callback);
  }

  offAllNotificationsCleared(): void {
    this.connection.off("AllNotificationsCleared");
  }

  /**
   * Авто-триггер создания лобби. Backend шлёт за 5 мин до старта матча
   * хосту (капитан Participant1). Если запущен Tauri — фронт автоматически
   * дёргает `create_tournament_lobby`. Иначе — обычное уведомление.
   */
  onMatchAutoLobbyTrigger(
    callback: (payload: MatchAutoLobbyTriggerPayload) => void,
  ): void {
    this.connection.on("MatchAutoLobbyTrigger", callback);
  }

  offMatchAutoLobbyTrigger(): void {
    this.connection.off("MatchAutoLobbyTrigger");
  }

  getConnectionState(): signalR.HubConnectionState {
    return this.connection.state;
  }
}
